import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { CRYPTO_SYMBOLS } from '../utils/constants';

const Hero: React.FC = () => {
  const [cryptoPrices, setCryptoPrices] = useState<{[key: string]: boolean}>({});
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const cryptoCards = CRYPTO_SYMBOLS;

  const blockchainBlocks = [1, 2, 3, 4];

  // Simulate crypto price changes
  useEffect(() => {
    const interval = setInterval(() => {
      cryptoCards.forEach(crypto => {
        setCryptoPrices(prev => ({
          ...prev,
          [crypto.symbol]: Math.random() > 0.3
        }));
      });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="home" className="hero" ref={ref}>
      <div className="hero-background">
        <motion.div
          className="hero-bg-gradient"
          animate={{
            background: [
              "radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, rgba(255, 215, 0, 0.1) 0%, transparent 50%)",
              "radial-gradient(circle at 40% 80%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)"
            ]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      <div className="hero-container">
        <motion.div
          className="hero-content"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.div className="hero-text" variants={itemVariants}>
            <motion.h1
              className="hero-name"
              variants={itemVariants}
            >
              Hodlwarden
            </motion.h1>
            
            <motion.h2
              className="hero-title"
              variants={itemVariants}
            >
              Full-stack Web3 AI Dev
            </motion.h2>
            
            <motion.div
              className="hero-tech-stack"
              variants={itemVariants}
            >
              Rust | Solidity | AWS | React | Next.js | Node.js
            </motion.div>
            
            <motion.p
              className="hero-description"
              variants={itemVariants}
            >
              Computer Science Master's Candidate Specializing in Research and Development within 
              the Blockchain Ecosystem. My focus encompasses leading technologies such as Bitcoin, 
              Ethereum Virtual Machine, Solana, Aptos, and Sui. With a passion for technology that 
              began in my youth, I have actively contributed to the development of various decentralized 
              applications (DApps), including NFTs, DeFi platforms, and Play-to-Earn (P2E) games. 
              Currently, my efforts are directed towards innovation in trading bot development, 
              specifically in areas like copy-trading, volume-bot, bundler-bot, and arbitrage-bot 
              solutions. I am also deeply engaged in pioneering projects at the intersection of AI 
              and blockchain, particularly in the creation of AI Agents for meme coin launches.
            </motion.p>
            
            <motion.div
              className="hero-buttons"
              variants={itemVariants}
            >
              <motion.a
                href="#projects"
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                View Projects
              </motion.a>
              <motion.a
                href="#contact"
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Get In Touch
              </motion.a>
            </motion.div>
          </motion.div>

          <motion.div className="hero-visual" variants={itemVariants}>
            <motion.div
              className="crypto-grid"
              variants={containerVariants}
            >
              {cryptoCards.map((crypto, index) => (
                <motion.div
                  key={crypto.symbol}
                  className={`crypto-card ${cryptoPrices[crypto.symbol] ? 'price-up' : 'price-down'}`}
                  variants={itemVariants}
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 3,
                    delay: index * 0.5,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                  whileHover={{ scale: 1.1, rotate: 360 }}
                >
                  {crypto.name === 'Solana' ? (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 256 256"
                      aria-label="Solana"
                      role="img"
                    >
                      <defs>
                        <linearGradient id="solGradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#9945FF" />
                          <stop offset="100%" stopColor="#14F195" />
                        </linearGradient>
                        <linearGradient id="solGradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#14F195" />
                          <stop offset="100%" stopColor="#00FFA3" />
                        </linearGradient>
                      </defs>
                      <g transform="translate(20,32)">
                        <path d="M26 0h170c6 0 9 7 5 11L171 40c-2 2-5 3-8 3H-7c-6 0-9-7-5-11L18 3c2-2 5-3 8-3z" fill="url(#solGradient1)" />
                        <path d="M26 94h170c6 0 9 7 5 11l-30 29c-2 2-5 3-8 3H-7c-6 0-9-7-5-11l30-29c2-2 5-3 8-3z" fill="url(#solGradient2)" />
                        <path d="M26 47h170c6 0 9 7 5 11l-30 29c-2 2-5 3-8 3H-7c-6 0-9-7-5-11l30-29c2-2 5-3 8-3z" fill="url(#solGradient1)" />
                      </g>
                    </svg>
                  ) : crypto.name === 'Binance Coin' || crypto.symbol === 'BNB' ? (
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 256 256"
                      aria-label="Binance Coin"
                      role="img"
                    >
                      <g fill="#F3BA2F">
                        <path d="M95.6 91.5L128 59.1l32.4 32.4L176 75.9 128 28 80 75.9z" />
                        <path d="M59.1 128l16.5-16.4L59.1 95.1 28 128l31.1 32.9 16.5-16.5z" />
                        <path d="M196.9 95.1l-16.5 16.5 16.5 16.4L228 128l-31.1-32.9z" />
                        <path d="M95.6 164.5L80 180.1 128 228l48-47.9-15.6-15.6L128 196.9z" />
                        <path d="M160.9 128L128 95.1 95.1 128 128 160.9z" />
                      </g>
                    </svg>
                  ) : crypto.icon ? (
                    <i className={crypto.icon} aria-label={crypto.name} />
                  ) : (
                    crypto.symbol
                  )}
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              className="blockchain-animation"
              variants={containerVariants}
            >
              {blockchainBlocks.map((block, index) => (
                <motion.div
                  key={block}
                  className="block"
                  variants={itemVariants}
                  animate={{
                    scaleY: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2,
                    delay: index * 0.2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;

